import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    List as ListIcon,
    ListChecks,
    MoreVertical,
    ChevronDown,
    Download,
    CheckSquare,
    SlidersHorizontal,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useTasks } from "../hooks/useTasks";
import { useTaskLists, useTaskEmployees } from "../hooks/useTaskLists";
import TaskQuickAdd from "./TaskQuickAdd";
import TaskListFilters from "./TaskListFilters";
import TaskRow from "./TaskRow";
import TaskDetailDrawer from "./TaskDetailDrawer";
import TaskListsSection from "./TaskListsSection";
import ListColorModal from "./ListColorModal";
import StaffTasksEmpty from "./StaffTasksEmpty";
import TaskViewTabs from "./TaskViewTabs";
import TaskAssignmentView from "./TaskAssignmentView";
import TaskAnalyticsPanel from "./TaskAnalyticsPanel";
import BulkAssignBar from "./BulkAssignBar";
import TaskCommandPalette, { useTaskCommandShortcut } from "./TaskCommandPalette";
import { groupTasksByAssignee } from "../utils/taskStats";
import { exportTasksToCsv } from "../utils/taskExport";
import { requestTaskNotificationPermission } from "../utils/taskNotifications";
import "../tasks-layout.css";

const TasksPage = ({
    role,
    apiUrl,
    getToken,
    TaskModal,
    TaskModalEdit,
    TaskHistory,
    EmptyStateBlock,
}) => {
    const token = getToken();
    const location = useLocation();
    const isStaff = role === "staff";
    const isAdmin = !isStaff;

    const [menuOpen, setMenuOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
    const [taskEdit, setTaskEdit] = useState(null);
    const [adminView, setAdminView] = useState("list");
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [paletteOpen, setPaletteOpen] = useState(false);
    const quickAddInputRef = useRef(null);

    const taskState = useTasks({ role, apiUrl, getToken });
    const listState = useTaskLists({ role, apiUrl, getToken });
    const { assignOptions } = useTaskEmployees({ role, apiUrl, getToken });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const assignmentGroups = useMemo(
        () =>
            isAdmin
                ? groupTasksByAssignee(
                      taskState.tasks.filter((t) => t.status !== "Completed"),
                      assignOptions
                  )
                : [],
        [isAdmin, taskState.tasks, assignOptions]
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("openTask") === "true") {
            window.history.replaceState({}, "", window.location.pathname);
            setIsModalOpen(true);
        }
        const filter = params.get("filter");
        if (filter && isAdmin) {
            if (filter === "today") {
                taskState.setDueFilter("today");
                taskState.setStatusFilter("active");
            } else if (filter === "overdue") {
                taskState.setDueFilter("overdue");
                taskState.setStatusFilter("active");
            } else if (filter === "active") {
                taskState.setDueFilter("active");
                taskState.setStatusFilter("active");
            }
            setAdminView("list");
        }
    }, [location, isAdmin]);

    useEffect(() => {
        if (isStaff) requestTaskNotificationPermission();
    }, [isStaff]);

    const openPalette = useCallback(() => setPaletteOpen(true), []);
    useTaskCommandShortcut(openPalette);

    const handleExportClick = useCallback(() => {
        exportTasksToCsv(taskState.filteredTasks);
        toast.success("Tasks exported to CSV");
        setMenuOpen(false);
    }, [taskState.filteredTasks]);

    const handleCommandAction = useCallback(
        (id, meta) => {
            switch (id) {
                case "focus-add":
                    quickAddInputRef.current?.focus();
                    break;
                case "advanced":
                    setIsModalOpen(true);
                    break;
                case "filter-today":
                    if (isStaff) {
                        taskState.setViewFilter("today");
                    } else {
                        taskState.setDueFilter("today");
                        taskState.setStatusFilter("active");
                        setAdminView("list");
                    }
                    break;
                case "filter-overdue":
                    taskState.setDueFilter("overdue");
                    taskState.setStatusFilter("active");
                    setAdminView("list");
                    break;
                case "filter-active":
                    if (isStaff) {
                        taskState.setViewFilter("all");
                    } else {
                        taskState.setDueFilter("active");
                        taskState.setStatusFilter("active");
                        setAdminView("list");
                    }
                    break;
                case "export":
                    handleExportClick();
                    break;
                case "view-list":
                    setAdminView("list");
                    break;
                case "view-assignments":
                    setAdminView("assignments");
                    break;
                case "view-analytics":
                    setAdminView("analytics");
                    break;
                case "bulk-assign":
                    setSelectionMode(true);
                    setSelectedIds(new Set());
                    setAdminView("list");
                    break;
                case "view-today":
                    taskState.setViewFilter("today");
                    break;
                case "view-all":
                    taskState.setViewFilter("all");
                    break;
                default:
                    if (id.startsWith("assign-")) {
                        taskState.setAssigneeFilter(
                            String(meta?.assignTo ?? id.replace("assign-", ""))
                        );
                        setAdminView("list");
                    }
            }
        },
        [isStaff, taskState, handleExportClick]
    );

    const handleEditTask = (task) => {
        setTaskEdit(task);
        setIsModalOpenEdit(true);
    };

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const handleBulkAssign = (assignTo) => {
        taskState.bulkAssign([...selectedIds], assignTo);
        setSelectedIds(new Set());
        setSelectionMode(false);
    };

    const handleMenuAction = (action) => {
        if (action === "export") handleExportClick();
        if (action === "analytics") {
            setAdminView("analytics");
            setMenuOpen(false);
        }
        if (action === "select") {
            setSelectionMode((p) => !p);
            setSelectedIds(new Set());
            setAdminView("list");
            setMenuOpen(false);
        }
    };

    const activeHeading = isStaff
        ? taskState.viewFilter === "today"
            ? `Due today (${taskState.activeTasks.length})`
            : `My tasks (${taskState.activeTasks.length})`
        : taskState.assigneeFilter
          ? `Filtered tasks (${taskState.activeTasks.length})`
          : taskState.dueFilter === "today"
            ? `Due today (${taskState.activeTasks.length})`
            : taskState.dueFilter === "overdue"
              ? `Overdue (${taskState.activeTasks.length})`
              : `Active tasks (${taskState.activeTasks.length})`;

    const quickAddProps = {
        role,
        apiUrl,
        token,
        assignOptions,
        onTaskCreated: taskState.fetchTasks,
        inputRef: quickAddInputRef,
    };

    const hasDrawer = Boolean(taskState.selectedTask);
    const showMobileBar = isStaff || isAdmin;

    const renderTaskList = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={taskState.handleDragEnd}
        >
            <SortableContext
                items={taskState.filteredTasks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
            >
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-sm font-semibold text-ink">{activeHeading}</div>
                        {isAdmin && selectionMode && (
                            <button
                                type="button"
                                onClick={() => {
                                    const all = taskState.activeTasks.map((t) => t.id);
                                    setSelectedIds(
                                        selectedIds.size === all.length
                                            ? new Set()
                                            : new Set(all)
                                    );
                                }}
                                className="text-xs text-primary font-medium hover:underline min-h-[44px] px-2"
                            >
                                {selectedIds.size === taskState.activeTasks.length
                                    ? "Deselect all"
                                    : "Select all"}
                            </button>
                        )}
                    </div>
                    {taskState.activeTasks.map((task) => (
                        <TaskRow
                            key={task.id}
                            task={task}
                            role={role}
                            onComplete={taskState.handleCompleteTask}
                            onEdit={handleEditTask}
                            onDelete={taskState.handleDeleteTask}
                            onView={taskState.setSelectedTask}
                            onTogglePause={taskState.handleTogglePause}
                            selectable={isAdmin && selectionMode}
                            selected={selectedIds.has(task.id)}
                            onSelectChange={toggleSelect}
                        />
                    ))}
                    {taskState.activeTasks.length === 0 &&
                        (isStaff ? (
                            <StaffTasksEmpty viewFilter={taskState.viewFilter} />
                        ) : EmptyStateBlock ? (
                            <EmptyStateBlock
                                compact
                                Icon={ListChecks}
                                title="No active tasks"
                                description="Create a task above or adjust your filters."
                                className="max-w-full !mt-0 border-dashed"
                            />
                        ) : (
                            <div className="rounded-lg border border-dashed border-line p-6 text-center text-ink-muted text-sm">
                                No active tasks. Add one above.
                            </div>
                        ))}

                    {taskState.completedTasks.length > 0 && (
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => taskState.setCompletedOpen((p) => !p)}
                                className="flex items-center gap-2 text-sm font-semibold text-ink-secondary hover:text-ink w-full min-h-[44px]"
                            >
                                <ChevronDown
                                    size={16}
                                    className={`transition-transform ${
                                        taskState.completedOpen ? "rotate-180" : ""
                                    }`}
                                />
                                Completed ({taskState.completedTasks.length})
                            </button>
                            <AnimatePresence>
                                {taskState.completedOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-2 space-y-2"
                                    >
                                        {taskState.completedTasks.map((task) => (
                                            <TaskRow
                                                key={task.id}
                                                task={task}
                                                role={role}
                                                onComplete={taskState.handleCompleteTask}
                                                onEdit={handleEditTask}
                                                onDelete={taskState.handleDeleteTask}
                                                onView={taskState.setSelectedTask}
                                                onTogglePause={taskState.handleTogglePause}
                                            />
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </SortableContext>
        </DndContext>
    );

    return (
        <div className={`tasks-page ${showMobileBar ? "tasks-page--mobile-bar" : ""}`}>
            <header className="tasks-page__header">
                <div className="tasks-page__header-start">
                    <h1 className="dashboard-title">{isStaff ? "My Tasks" : "Tasks"}</h1>
                    <button
                        type="button"
                        onClick={openPalette}
                        className="hidden sm:inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] text-ink-muted hover:bg-surface-2 transition"
                        title="Command palette (Ctrl+K)"
                    >
                        <span>⌘K</span>
                    </button>
                </div>

                <div className="tasks-page__actions relative">
                    <button
                        type="button"
                        onClick={() => listState.setListSectionOpen((p) => !p)}
                        className="oms-add-btn min-h-[44px]"
                    >
                        <span className="hidden sm:inline">List</span>
                        <ListIcon className="w-4 h-4" />
                    </button>

                    {isAdmin && (
                        <>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="hidden md:inline-flex oms-add-btn min-h-[44px]"
                            >
                                <span>Advanced</span>
                                <SlidersHorizontal className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-surface-2 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                                onClick={() => setMenuOpen((p) => !p)}
                                aria-label="More task actions"
                            >
                                <MoreVertical className="w-5 h-5 text-ink-secondary" />
                            </button>
                            <AnimatePresence>
                                {menuOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setMenuOpen(false)}
                                            aria-hidden
                                        />
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            className="absolute right-0 top-full mt-2 w-52 bg-surface shadow-xl rounded-xl overflow-hidden z-50 border border-line"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("export")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <Download size={14} /> Export CSV
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("select")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <CheckSquare size={14} />
                                                {selectionMode ? "Cancel selection" : "Bulk assign"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("analytics")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                View analytics
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsModalOpen(true);
                                                    setMenuOpen(false);
                                                }}
                                                className="flex md:hidden items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <SlidersHorizontal size={14} />
                                                Advanced create
                                            </button>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </div>
            </header>

            <TaskListsSection
                isOpen={listState.listSectionOpen}
                lists={listState.lists}
                activeListId={listState.activeListId}
                setActiveListId={listState.setActiveListId}
                onCreateClick={() => {
                    listState.setEditList(null);
                    listState.setListName("");
                    listState.setSelectedColor(null);
                    listState.setPopupOpen(true);
                }}
                onEditClick={(list) => {
                    listState.setEditList(list);
                    listState.setPopupOpen(true);
                }}
                onDeleteList={listState.handleDeleteList}
            />

            <ListColorModal
                isOpen={listState.popupOpen}
                onClose={() => {
                    listState.setPopupOpen(false);
                    listState.setEditList(null);
                }}
                listName={listState.listName}
                setListName={listState.setListName}
                availableColors={listState.availableColors}
                selectedColor={listState.selectedColor}
                setSelectedColor={listState.setSelectedColor}
                onSubmit={listState.handleCreateList}
                isEdit={!!listState.editList}
            />

            <div className="tasks-page__workspace">
                <div
                    className={`tasks-page__grid ${hasDrawer ? "tasks-page__grid--split" : ""}`}
                >
                    <div className="tasks-page__main text-xs md:text-[13px]">
                        <div className="tasks-page__toolbar">
                            <div className="hidden md:block">
                                <TaskQuickAdd {...quickAddProps} />
                            </div>

                            {isStaff && (
                                <TaskListFilters
                                    role={role}
                                    statusFilter={taskState.statusFilter}
                                    setStatusFilter={taskState.setStatusFilter}
                                    priorityFilter={taskState.priorityFilter}
                                    setPriorityFilter={taskState.setPriorityFilter}
                                    viewFilter={taskState.viewFilter}
                                    setViewFilter={taskState.setViewFilter}
                                    todayCount={taskState.todayCount}
                                    onAdvancedClick={() => setIsModalOpen(true)}
                                    onCompletedFilter={() => taskState.setCompletedOpen(true)}
                                />
                            )}

                            {isAdmin && (
                                <TaskViewTabs active={adminView} onChange={setAdminView} />
                            )}
                        </div>

                        {adminView === "list" && (
                            <>
                                {isAdmin && (
                                    <TaskListFilters
                                        role={role}
                                        statusFilter={taskState.statusFilter}
                                        setStatusFilter={taskState.setStatusFilter}
                                        priorityFilter={taskState.priorityFilter}
                                        setPriorityFilter={taskState.setPriorityFilter}
                                        viewFilter={taskState.viewFilter}
                                        setViewFilter={taskState.setViewFilter}
                                        todayCount={taskState.todayCount}
                                        onAdvancedClick={() => setIsModalOpen(true)}
                                        onCompletedFilter={() => taskState.setCompletedOpen(true)}
                                    />
                                )}
                                {(taskState.dueFilter || taskState.assigneeFilter) && isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            taskState.setDueFilter("");
                                            taskState.setAssigneeFilter("");
                                        }}
                                        className="text-xs text-primary hover:underline mb-2 min-h-[44px]"
                                    >
                                        Clear filters
                                    </button>
                                )}
                                {renderTaskList()}
                                {isAdmin && (
                                    <BulkAssignBar
                                        selectedCount={selectedIds.size}
                                        assignOptions={assignOptions}
                                        onAssign={handleBulkAssign}
                                        onClear={() => {
                                            setSelectedIds(new Set());
                                            setSelectionMode(false);
                                        }}
                                    />
                                )}
                            </>
                        )}

                        {isAdmin && adminView === "assignments" && (
                            <TaskAssignmentView
                                groups={assignmentGroups}
                                onSelectTask={taskState.setSelectedTask}
                                onSelectGroup={(assignTo) => {
                                    taskState.setAssigneeFilter(String(assignTo));
                                    setAdminView("list");
                                }}
                            />
                        )}

                        {isAdmin && adminView === "analytics" && (
                            <TaskAnalyticsPanel tasks={taskState.tasks} />
                        )}
                    </div>

                    <AnimatePresence>
                        {taskState.selectedTask && (
                            <div className="tasks-page__aside">
                                <TaskDetailDrawer
                                    task={taskState.selectedTask}
                                    onClose={() => taskState.setSelectedTask(null)}
                                    onUpdateField={taskState.updateTaskField}
                                    leadOptions={listState.leadOptions}
                                    assignOptions={assignOptions}
                                    apiUrl={apiUrl}
                                    role={role}
                                    TaskHistory={TaskHistory}
                                />
                            </div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {showMobileBar && (
                <div className="tasks-page__mobile-bar md:hidden">
                    <div className="tasks-page__mobile-bar-inner">
                        <TaskQuickAdd {...quickAddProps} />
                        {isAdmin && (
                            <div className="tasks-page__mobile-bar-actions">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(true)}
                                    className="tasks-page__mobile-advanced-btn"
                                >
                                    <SlidersHorizontal size={16} />
                                    Advanced
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {TaskModal && (
                <TaskModal
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                    leadOptions={listState.leadOptions}
                    assignOptions={assignOptions}
                    fetchTask={taskState.fetchTasks}
                />
            )}
            {TaskModalEdit && (
                <TaskModalEdit
                    isOpen={isModalOpenEdit}
                    setIsOpen={setIsModalOpenEdit}
                    leadOptions={listState.leadOptions}
                    assignOptions={assignOptions}
                    taskEdit={taskEdit}
                    fetchTask={taskState.fetchTasks}
                />
            )}

            <TaskCommandPalette
                isOpen={paletteOpen}
                onClose={() => setPaletteOpen(false)}
                role={role}
                onAction={handleCommandAction}
                assignOptions={assignOptions}
            />
        </div>
    );
};

export default TasksPage;
